import React, { useState } from "react";
import {
  Calendar,
  Check,
  Link,
  Paperclip,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Trash2,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tag } from "../../../types";
import { splitGraphemes } from "../../../lib/utils";

export interface SortableSubtaskRowProps {
  id: string;
  title: string;
  completed?: boolean;
  priority?: number;
  scheduledDate?: string | null;
  scheduledTime?: string | null;
  tags?: Tag[];
  hasAttachments?: boolean;
  hasLinks?: boolean;
  isEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  isLastItem: boolean;
  onToggle?: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onNavigateToSubtask?: () => void;
}

const SortableSubtaskRow: React.FC<SortableSubtaskRowProps> = ({
  id,
  title,
  completed,
  priority,
  scheduledDate,
  scheduledTime,
  tags,
  hasAttachments,
  hasLinks,
  isEdit,
  isFirst,
  isLast,
  isLastItem,
  onToggle,
  onMoveUp,
  onMoveDown,
  onDelete,
  onNavigateToSubtask,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const [hoveringCheckbox, setHoveringCheckbox] = useState(false);
  const showHoverPreview = isEdit && hoveringCheckbox && !completed;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`subtask-row-${id}`}
      data-priority={priority}
      className={`flex items-center gap-2 px-3 py-2.5 hover:bg-warm-border-subtle transition-colors group ${
        !isLastItem ? "border-b border-warm-border-subtle" : ""
      } ${!completed && priority === 1 ? "border-l-4 border-l-red-700" : ""} ${!completed && priority === 2 ? "border-l-4 border-l-orange-500" : ""} ${!completed && priority === 3 ? "border-l-4 border-l-yellow-400" : ""}`}
    >
      <div
        className="flex-shrink-0 w-[14px] text-warm-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={`Reorder "${title}"`}
      >
        <GripVertical size={14} />
      </div>
      {isEdit ? (
        <button
          onClick={onToggle}
          onMouseEnter={() => setHoveringCheckbox(true)}
          onMouseLeave={() => setHoveringCheckbox(false)}
          onFocus={() => setHoveringCheckbox(true)}
          onBlur={() => setHoveringCheckbox(false)}
          aria-label={
            completed
              ? `Mark "${title}" incomplete`
              : `Mark "${title}" complete`
          }
          className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ease-in-out ${
            completed
              ? "bg-warm-blue border-warm-blue"
              : showHoverPreview
                ? "bg-warm-blue/25 border-warm-blue scale-110 shadow-soft-sm"
                : "border-warm-text-tertiary hover:border-warm-blue"
          }`}
        >
          {completed && <Check size={12} className="text-white" />}
          {showHoverPreview && (
            <Check
              size={12}
              strokeWidth={3}
              className="text-warm-blue opacity-80"
            />
          )}
        </button>
      ) : (
        <button
          disabled
          className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center border-warm-text-tertiary"
        />
      )}
      <div
        className={`flex-1 flex flex-col gap-1 min-w-0 ${onNavigateToSubtask ? "cursor-pointer" : ""}`}
        onClick={(e) => {
          if (onNavigateToSubtask) {
            e.stopPropagation();
            onNavigateToSubtask();
          }
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`text-sm flex-1 min-w-0 truncate ${
              completed
                ? "line-through text-warm-text-tertiary"
                : "text-warm-text-primary"
            } ${onNavigateToSubtask ? "hover:text-warm-blue dark:hover:text-warm-blue transition-colors" : ""} ${showHoverPreview ? "title-dissolve-active" : ""}`}
          >
            {completed
              ? title
              : splitGraphemes(title).map((ch, i, arr) => (
                  <span
                    key={i}
                    className="dissolve-char"
                    style={
                      {
                        "--char-delay": `${(arr.length - 1 - i) * 22}ms`,
                      } as React.CSSProperties
                    }
                  >
                    {ch === " " ? " " : ch}
                  </span>
                ))}
          </span>
        </div>
        {/* Tags, Scheduled Date, Attachment & Link Chips */}
        {(scheduledDate || (tags && tags.length > 0) || hasAttachments || hasLinks) && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {hasAttachments && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-warm-bg-elevated dark:bg-warm-bg-base text-warm-text-tertiary flex-shrink-0 flex items-center">
                <Paperclip size={10} />
              </span>
            )}
            {hasLinks && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-warm-bg-elevated dark:bg-warm-bg-base text-warm-text-tertiary flex-shrink-0 flex items-center">
                <Link size={10} />
              </span>
            )}
            {scheduledDate && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-warm-bg-elevated dark:bg-warm-bg-base text-warm-text-tertiary flex-shrink-0 flex items-center gap-1">
                <Calendar size={10} />
                {new Date(scheduledDate + "T00:00:00").toLocaleDateString(
                  "en-US",
                  { month: "short", day: "numeric" },
                )}
                {scheduledTime && ` ${scheduledTime}`}
              </span>
            )}
            {tags && tags.length > 0 && (
              <div className="flex items-center gap-1">
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white flex-shrink-0"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Action buttons — collapse to 0 width when not hovered so they don't steal space from the title */}
      <div className="flex-shrink-0 flex items-center gap-0.5 w-0 overflow-hidden group-hover:w-auto group-hover:overflow-visible">
        <button
          onClick={onMoveUp}
          disabled={isFirst}
          aria-label={`Move "${title}" up`}
          className="flex-shrink-0 text-warm-text-tertiary hover:text-warm-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronUp size={14} />
        </button>
        <button
          onClick={onMoveDown}
          disabled={isLast}
          aria-label={`Move "${title}" down`}
          className="flex-shrink-0 text-warm-text-tertiary hover:text-warm-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronDown size={14} />
        </button>
        <button
          onClick={onDelete}
          aria-label={`Delete subtask "${title}"`}
          className="flex-shrink-0 text-warm-text-tertiary hover:text-red-500 dark:hover:text-red-400 transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

export default SortableSubtaskRow;
